/**
 * Real-Time Token Arbitrage (public landing)
 *
 * Live LLM provider pricing index across 12 providers + cheapest-quality-adjusted
 * routing per task class + spot-burst detection + savings simulator.
 *
 * This is THE flagship engine of CARTIEAI v34.x. Public, SEO-friendly,
 * shareable. Pulls live data from /api/engine27/{prices, savings, bursts}.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, TrendingDown, AlertTriangle, Activity, Network, Sparkles,
  ChevronRight, Clock, Award, Cpu, ArrowRight,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import AutoCompilerSection from '../components/AutoCompilerSection';
import { ENGINE_COUNT } from '../constants/product';

const API = process.env.REACT_APP_BACKEND_URL;
const E27 = `${API}/api/engine27`;

const TASK_CLASSES = [
  { id: 'reasoning',      label: 'Reasoning',      desc: 'Multi-step logic, math, planning' },
  { id: 'code',           label: 'Code',           desc: 'Generation, completion, review' },
  { id: 'summarization',  label: 'Summarization',  desc: 'Long-form → short-form digest' },
  { id: 'classification', label: 'Classification', desc: 'Label / route / extract intent' },
  { id: 'retrieval',      label: 'RAG / Retrieval',desc: 'Doc-augmented Q&A' },
];

const PROVIDER_COLORS = {
  openai: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  anthropic: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  google: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  groq: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  together: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  fireworks: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  deepinfra: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  anyscale: 'bg-teal-500/15 text-teal-300 border-teal-500/30',
  perplexity: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  mistral: 'bg-pink-500/15 text-pink-300 border-pink-500/30',
  cohere: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
  bedrock: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
};

const fmt$ = (n, frac = 4) => '$' + Number(n).toFixed(frac);
const fmtPct = (n) => (n >= 0 ? '+' : '') + n.toFixed(1) + '%';

export default function Engine27() {
  const [prices, setPrices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [bursts, setBursts] = useState([]);
  const [taskClass, setTaskClass] = useState('summarization');
  const [routing, setRouting] = useState(null);
  const [savings, setSavings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [routeLoading, setRouteLoading] = useState(false);
  const [monthlyTokens, setMonthlyTokens] = useState(10_000_000);

  // Initial load
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [pricesRes, providersRes, burstsRes] = await Promise.all([
          fetch(`${E27}/prices`).then((r) => r.json()),
          fetch(`${E27}/providers`).then((r) => r.json()),
          fetch(`${E27}/bursts?hours=24`).then((r) => r.json()),
        ]);
        if (!active) return;
        setPrices(pricesRes.prices || []);
        setProviders(providersRes.providers || []);
        setBursts(burstsRes.bursts || []);
      } catch (e) {
        console.error('Engine27 load failed', e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Recompute routing + savings when task class or monthly tokens change.
  useEffect(() => {
    let active = true;
    (async () => {
      setRouteLoading(true);
      try {
        const [routeRes, savRes] = await Promise.all([
          fetch(`${E27}/route`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ task_class: taskClass, estimated_tokens: 5000 }),
          }).then((r) => r.json()),
          fetch(`${E27}/savings?task_class=${taskClass}&monthly_tokens=${monthlyTokens}`)
            .then((r) => r.json()),
        ]);
        if (!active) return;
        setRouting(routeRes);
        setSavings(savRes);
      } catch (e) {
        console.error('routing recompute failed', e);
      } finally {
        if (active) setRouteLoading(false);
      }
    })();
    return () => { active = false; };
  }, [taskClass, monthlyTokens]);

  const cheapest3 = useMemo(() => {
    const sorted = [...prices]
      .filter((p) => p.status !== 'outage')
      .sort((a, b) => (a.input_per_1k + a.output_per_1k) - (b.input_per_1k + b.output_per_1k));
    return sorted.slice(0, 3);
  }, [prices]);

  // "Active right now" = bursts whose window has not expired yet.
  // A burst lasts `duration_min` minutes from `started_at`.
  const activeBursts = useMemo(() => {
    const now = Date.now();
    return bursts.filter((b) => {
      if (!b.started_at || !b.duration_min) return false;
      const startedMs = Date.parse(b.started_at);
      if (Number.isNaN(startedMs)) return false;
      return now - startedMs < b.duration_min * 60 * 1000;
    }).length;
  }, [bursts]);

  const totalProviders = providers.length;
  const totalModels = prices.length;

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="engine27-page">
      <SEOHead
        title="Real-Time Token Arbitrage — CARTIEAI"
        description="Live LLM provider pricing across 12 providers. Cheapest-quality-adjusted routing for code, reasoning, summarization, classification, and RAG. Spot-burst detection in real time."
        canonical="https://cartieai.com/engine-27"
        keywords="LLM token arbitrage, token routing, real-time LLM pricing, cheapest LLM, GPT-5 vs Claude, model routing, CARTIE auto-compiler proxy"
      />

      {/* HERO */}
      <section className="relative pt-24 pb-12 px-4 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.12),transparent_60%)]" />
        <div className="max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-mono mb-5" data-testid="engine27-badge">
            <Sparkles className="w-3.5 h-3.5" /> Real-Time Token Arbitrage · Live
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" data-testid="engine27-h1">
            Token Arbitrage <span className="text-emerald-400">in real time.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
            We watch <span className="text-white font-semibold">12 LLM providers</span> across <span className="text-white font-semibold">{totalModels || 30} models</span>, detect spot bursts within seconds, and route every token to the cheapest provider that still meets your quality bar.
          </p>

          {/* Live KPI strip */}
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="engine27-kpis">
            <KpiCard icon={Network} label="Providers tracked" value={totalProviders || '—'} accent="emerald" testid="kpi-providers" />
            <KpiCard icon={Cpu} label="Models priced live" value={totalModels || '—'} accent="sky" testid="kpi-models" />
            <KpiCard icon={Activity} label="Spot bursts (24h)" value={bursts.length} accent="amber" testid="kpi-bursts" />
            <KpiCard icon={AlertTriangle} label="Active right now" value={activeBursts} accent="rose" testid="kpi-active-bursts" />
          </div>
        </div>
      </section>

      {/* ROUTING DEMO */}
      <section className="px-4 py-14 border-b border-white/5" data-testid="engine27-routing-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Live routing decision</h2>
          <p className="mt-2 text-sm text-slate-400 max-w-2xl">
            Pick a task class. We evaluate every model against the quality floor for that task and return the cheapest viable provider — recomputed on every price tick.
          </p>

          {/* Task class picker */}
          <div className="mt-6 flex flex-wrap gap-2" role="tablist" data-testid="engine27-task-tabs">
            {TASK_CLASSES.map((tc) => (
              <button
                key={tc.id}
                onClick={() => setTaskClass(tc.id)}
                role="tab"
                aria-selected={taskClass === tc.id}
                data-testid={`task-tab-${tc.id}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                  taskClass === tc.id
                    ? 'bg-emerald-500 border-emerald-400 text-slate-950'
                    : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                }`}
              >
                {tc.label}
              </button>
            ))}
          </div>

          {/* Winner + baseline cards */}
          <div className="mt-6 grid lg:grid-cols-2 gap-4">
            <WinnerCard routing={routing} loading={routeLoading} />
            <BaselineCard routing={routing} loading={routeLoading} />
          </div>

          {/* Savings projection */}
          <div className="mt-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5" data-testid="engine27-savings-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-wider font-mono mb-1">
                  <TrendingDown className="w-3.5 h-3.5" /> Projected savings vs always-use-baseline
                </div>
                <div className="text-3xl sm:text-4xl font-extrabold text-white tabular-nums" data-testid="savings-amount">
                  {savings ? `$${Math.round(savings.savings_per_month_usd).toLocaleString()}` : '—'}
                  <span className="text-base text-slate-400 font-medium ml-2">/month</span>
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  {savings ? `${savings.savings_pct.toFixed(1)}% reduction · routing every ${taskClass} request through the arbitrage engine` : 'Recomputing…'}
                </div>
              </div>
              <div className="text-right">
                <label className="text-xs text-slate-400 block mb-1">Monthly tokens</label>
                <select
                  value={monthlyTokens}
                  onChange={(e) => setMonthlyTokens(Number(e.target.value))}
                  className="bg-slate-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                  data-testid="monthly-tokens-select"
                >
                  <option value={1_000_000}>1M tokens</option>
                  <option value={10_000_000}>10M tokens</option>
                  <option value={50_000_000}>50M tokens</option>
                  <option value={100_000_000}>100M tokens</option>
                  <option value={500_000_000}>500M tokens</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUTO-COMPILER v2 — flagship pitch */}
      <AutoCompilerSection />

      {/* CHEAPEST 3 + SPOT BURSTS */}
      <section className="px-4 py-14 border-b border-white/5">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-6">
          {/* Cheapest 3 */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="engine27-cheapest3-card">
            <h3 className="text-sm font-bold text-white tracking-wider uppercase font-mono flex items-center gap-2">
              <Award className="w-4 h-4 text-emerald-400" /> Cheapest 3 right now
            </h3>
            <div className="mt-4 space-y-3">
              {loading && <div className="text-sm text-slate-500">Loading prices…</div>}
              {!loading && cheapest3.map((p, idx) => (
                <div key={p.model_key} className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/60 border border-white/5" data-testid={`cheapest-row-${idx}`}>
                  <div className="text-2xl font-extrabold text-emerald-400 w-8 tabular-nums">#{idx + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold border ${PROVIDER_COLORS[p.provider_id] || 'bg-white/5 text-slate-300 border-white/10'}`}>
                        {p.provider_id}
                      </span>
                      <span className="text-sm font-mono text-white truncate">{p.model}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      in {fmt$(p.input_per_1k, 5)}/1K · out {fmt$(p.output_per_1k, 5)}/1K · {p.ttft_p50_ms}ms p50
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Spot bursts */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="engine27-bursts-card">
            <h3 className="text-sm font-bold text-white tracking-wider uppercase font-mono flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" /> Spot bursts · last 24h
            </h3>
            <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
              {loading && <div className="text-sm text-slate-500">Loading…</div>}
              {!loading && bursts.length === 0 && (
                <div className="text-sm text-slate-500 italic">No spot bursts detected in the last 24h. Markets are quiet.</div>
              )}
              {!loading && bursts.map((b, idx) => (
                <div key={`${b.model_key}-${idx}`} className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid={`burst-row-${idx}`}>
                  <TrendingDown className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold border ${PROVIDER_COLORS[b.provider_id] || 'bg-white/5 text-slate-300 border-white/10'}`}>
                        {b.provider_id}
                      </span>
                      <span className="text-sm font-mono text-white truncate">{b.model}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      <span className="text-amber-300 font-bold">−{b.price_drop_pct}%</span> · {b.duration_min}min window
                      {b.still_active && <span className="ml-2 text-emerald-400 font-semibold">· ACTIVE</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-4 py-14 border-b border-white/5" data-testid="engine27-how-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">How Real-Time Token Arbitrage works</h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-5">
            {[
              { n: '01', t: 'Watch', d: 'Every 30s we ingest pricing + TTFT from all 12 provider APIs.' },
              { n: '02', t: 'Score', d: 'Each model gets a quality-adjusted blended cost per task class. Models below the floor are dropped.' },
              { n: '03', t: 'Route', d: 'Your /route call hits the cheapest qualifying model. Spot bursts auto-trigger reroutes.' },
            ].map((s) => (
              <div key={s.n} className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid={`how-step-${s.n}`}>
                <div className="text-emerald-400 font-mono text-xs">{s.n}</div>
                <div className="text-lg font-bold text-white mt-1">{s.t}</div>
                <div className="text-sm text-slate-400 mt-2 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>

          {/* Slack alert callout */}
          <div className="mt-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5 sm:p-6" data-testid="engine27-slack-alerts">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-mono uppercase tracking-wider text-emerald-300">New in v34.99</div>
                <div className="text-lg font-bold text-white mt-1">Slack DMs the moment a ≥ 35% spot burst lands</div>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                  Every burst over the alert threshold sends a formatted Slack block — provider, model, drop %, duration window, and a deep-link back here.
                  Customize the threshold with <code className="text-emerald-300 bg-slate-900 px-1.5 py-0.5 rounded">ENGINE27_BURST_ALERT_THRESHOLD_PCT</code>.
                  No-op gracefully if no webhook is configured — failures never block a routing tick.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-16">
        <div className="max-w-4xl mx-auto rounded-3xl border border-white/10 bg-slate-900 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-slate-900 p-8 sm:p-10 text-center" data-testid="engine27-cta">
          <div className="text-xs font-mono uppercase tracking-wider text-emerald-300 mb-3">Stop paying frontier prices for non-frontier tasks</div>
          <h3 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
            Connect your stack. See your savings.
          </h3>
          <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-xl mx-auto">
            CARTIE plugs into your existing OpenAI / Anthropic / Gemini / Groq / Mistral / Bedrock SDK calls and routes them automatically. No code changes — just a base URL swap.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/pricing" data-testid="engine27-cta-pricing" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold transition">
              See pricing <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/patent-portfolio" data-testid="engine27-cta-engines" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-white/15 hover:border-white/30 text-white font-semibold transition">
              See all {ENGINE_COUNT} engines <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, accent = 'emerald', testid }) {
  const tone = {
    emerald: 'text-emerald-400',
    sky: 'text-sky-400',
    amber: 'text-amber-400',
    rose: 'text-rose-400',
  }[accent];
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 sm:p-4" data-testid={testid}>
      <div className={`flex items-center gap-2 text-xs ${tone}`}>
        <Icon className="w-3.5 h-3.5" />
        <span className="font-mono uppercase tracking-wider">{label}</span>
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-extrabold text-white tabular-nums">{value}</div>
    </div>
  );
}

function WinnerCard({ routing, loading }) {
  if (loading || !routing) {
    return (
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 min-h-[180px] flex items-center justify-center" data-testid="winner-card-loading">
        <div className="text-sm text-slate-500">Computing optimal route…</div>
      </div>
    );
  }
  const w = routing.winner;
  return (
    <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-5" data-testid="engine27-winner-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-wider font-mono">
          <Award className="w-3.5 h-3.5" /> Winner
        </div>
        <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold border ${PROVIDER_COLORS[w.provider_id] || 'bg-white/5 text-slate-300 border-white/10'}`}>
          {w.provider_id}
        </span>
      </div>
      <div className="mt-3 text-2xl font-extrabold text-white font-mono break-all" data-testid="winner-model">{w.model}</div>
      <div className="text-xs text-slate-400 mt-1">{w.tier} tier · quality {w.quality_score.toFixed(2)} · {w.ttft_p50_ms}ms p50</div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs text-slate-400">Blended cost / 1K</div>
          <div className="text-lg font-bold text-emerald-400 tabular-nums" data-testid="winner-cost">{fmt$(w.blended_cost_per_1k, 5)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-400">Savings vs baseline</div>
          <div className="text-lg font-bold text-emerald-400 tabular-nums" data-testid="winner-savings-pct">{fmtPct(routing.savings_pct)}</div>
        </div>
      </div>
      <div className="text-xs text-slate-500 mt-3 flex items-center gap-1">
        <Clock className="w-3 h-3" /> Evaluated {routing.considered} models · recomputed live
      </div>
    </div>
  );
}

function BaselineCard({ routing, loading }) {
  if (loading || !routing) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 min-h-[180px] flex items-center justify-center" data-testid="baseline-card-loading">
        <div className="text-sm text-slate-500">…</div>
      </div>
    );
  }
  const b = routing.baseline;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="engine27-baseline-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-mono">
          <AlertTriangle className="w-3.5 h-3.5" /> Always-use-frontier baseline
        </div>
        <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold border ${PROVIDER_COLORS[b.provider_id] || 'bg-white/5 text-slate-300 border-white/10'}`}>
          {b.provider_id}
        </span>
      </div>
      <div className="mt-3 text-2xl font-extrabold text-white font-mono break-all" data-testid="baseline-model">{b.model}</div>
      <div className="text-xs text-slate-400 mt-1">{b.tier} tier · the obvious-but-expensive default</div>
      <div className="mt-5">
        <div className="text-xs text-slate-400">Blended cost / 1K</div>
        <div className="text-lg font-bold text-slate-300 tabular-nums" data-testid="baseline-cost">{fmt$(b.blended_cost_per_1k, 5)}</div>
      </div>
      <div className="text-xs text-slate-500 mt-3 leading-relaxed">
        This is what you&apos;d pay if you always sent every request to the most-expensive frontier model. Most teams default to this.
      </div>
    </div>
  );
}
